import type { FlowTransferTokenHeader, FlowTransferTokenPayload, PortableFlowPackage } from './types';

export const FLOW_TOKEN_MAX_LENGTH = 1_000_000;
export const FLOW_TOKEN_MAX_NODES = 500;
export const FLOW_TOKEN_MAX_EDGES = 2_000;
const MAX_JSON_DEPTH = 50;

const HEADER: FlowTransferTokenHeader = { alg: 'none', typ: 'FLOW-JWT', v: 1 };

function sortKeysDeep(value: unknown): unknown {
	if (value === null || typeof value !== 'object') return value;
	if (Array.isArray(value)) return value.map(sortKeysDeep);
	const source = value as Record<string, unknown>;
	return Object.fromEntries(
		Object.keys(source)
			.sort()
			.map((key) => [key, sortKeysDeep(source[key])]),
	);
}

function stableStringify(value: unknown): string {
	return JSON.stringify(sortKeysDeep(value));
}

/** FNV-1a checksum: accidental-corruption detection only, never authenticity. */
export function checksumPortableFlow(flow: Omit<PortableFlowPackage, 'checksum'>): string {
	const input = stableStringify(flow);
	let hash = 0x811c9dc5;
	for (let index = 0; index < input.length; index += 1) {
		hash ^= input.charCodeAt(index);
		hash = Math.imul(hash, 0x01000193);
	}
	return (hash >>> 0).toString(16).padStart(8, '0');
}

function bytesToBase64Url(bytes: Uint8Array): string {
	let binary = '';
	for (let index = 0; index < bytes.length; index += 0x8000) {
		binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
	}
	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBytes(value: string): Uint8Array {
	if (!/^[A-Za-z0-9_-]*$/.test(value)) throw new Error('Token contains invalid Base64URL data');
	const padded = value
		.replace(/-/g, '+')
		.replace(/_/g, '/')
		.padEnd(Math.ceil(value.length / 4) * 4, '=');
	const binary = atob(padded);
	return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

function encodeJson(value: unknown): string {
	return bytesToBase64Url(new TextEncoder().encode(JSON.stringify(value)));
}

function decodeJson(value: string): unknown {
	const json = new TextDecoder().decode(base64UrlToBytes(value));
	return JSON.parse(json);
}

function assertSafeDepth(value: unknown, depth = 0): void {
	if (depth > MAX_JSON_DEPTH) throw new Error('Flow token is nested too deeply');
	if (!value || typeof value !== 'object') return;
	const children = Array.isArray(value) ? value : Object.values(value as Record<string, unknown>);
	for (const child of children) assertSafeDepth(child, depth + 1);
}

function validatePortableFlow(flow: unknown): asserts flow is PortableFlowPackage {
	if (!flow || typeof flow !== 'object') throw new Error('Flow payload is missing');
	const candidate = flow as Partial<PortableFlowPackage>;
	if (candidate.format !== 'shelly-flow' || candidate.formatVersion !== 1) {
		throw new Error('Unsupported Flow token version');
	}
	if (!Array.isArray(candidate.nodes) || !Array.isArray(candidate.edges)) {
		throw new Error('Flow token does not contain a graph');
	}
	if (candidate.nodes.length > FLOW_TOKEN_MAX_NODES) throw new Error('Flow token has too many nodes');
	if (candidate.edges.length > FLOW_TOKEN_MAX_EDGES) throw new Error('Flow token has too many edges');
	if (!Array.isArray(candidate.requirements) || !Array.isArray(candidate.warnings)) {
		throw new Error('Flow token manifest is invalid');
	}
	if (typeof candidate.checksum !== 'string') throw new Error('Flow token checksum is missing');
	assertSafeDepth(candidate);
	const { checksum, ...unsigned } = candidate as PortableFlowPackage;
	if (checksumPortableFlow(unsigned) !== checksum) {
		throw new Error('Flow token checksum does not match');
	}
}

export function encodeFlowToken(flow: PortableFlowPackage): string {
	validatePortableFlow(flow);
	const payload: FlowTransferTokenPayload = {
		iss: 'shelly-flow-editor',
		iat: Math.floor(Date.now() / 1000),
		flow,
	};
	const token = `${encodeJson(HEADER)}.${encodeJson(payload)}.`;
	if (token.length > FLOW_TOKEN_MAX_LENGTH) throw new Error('Flow token is too large to share');
	return token;
}

export function decodeFlowToken(rawToken: string): PortableFlowPackage {
	const token = rawToken.trim();
	if (!token) throw new Error('Paste a Flow token');
	if (token.length > FLOW_TOKEN_MAX_LENGTH) throw new Error('Flow token is too large');
	const parts = token.split('.');
	if (parts.length !== 3 || parts[2] !== '') throw new Error('Expected an unsigned Flow JWT');
	const header = decodeJson(parts[0]) as Partial<FlowTransferTokenHeader>;
	if (header.alg !== 'none' || header.typ !== 'FLOW-JWT' || header.v !== 1) {
		throw new Error('Unsupported Flow token header');
	}
	const payload = decodeJson(parts[1]) as Partial<FlowTransferTokenPayload>;
	if (payload.iss !== 'shelly-flow-editor') throw new Error('Unknown Flow token issuer');
	validatePortableFlow(payload.flow);
	return payload.flow;
}
